import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DatePickerComponent } from 'components/input/date-time-picker/date-picker.component';
import { TimePickerComponent } from 'components/input/date-time-picker/time-picker.component';
import { PanelInfoBarComponent } from 'components/layout/panel-info-bar/panel-info-bar.component';
import { EditSaleComponent } from 'components/task/salesflow/editsale.component';
import { TaskActionLogComponent } from 'components/task/taskflow/task-action-log.component';
import { TaskActionMailInputComponent } from 'components/task/taskflow/task-action-mail.component';
import { LogEntryStatus } from 'constants/LogEntryStatus';
import { InputType } from 'enums/InputType';
import { TaskActionType } from 'enums/TaskActionType';
import { CommunicationLogEntryModel } from 'models/generated/Communication.Api.Models.CommunicationLogEntry';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { ContactModel } from 'models/generated/Entities.GormCrm.Contact';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { TaskflowModel } from 'models/generated/Entities.GormCrm.Taskflow';
import { InfoBarIconModel, InfoBarModel } from 'models/InfoBar';
import { Time } from 'models/Time';
import { Inputs } from 'modules/inputs';
import { Layout } from 'modules/layout';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { CommunicationService } from 'services/communication.service';
import { CompanyService } from 'services/company.service';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { TaskService } from 'services/task.service';
import { UserService } from 'services/user.service';

@Component({
	selector: 'view-salesflow',
	templateUrl: './view-salesflow.component.html',
	//	providers: [FormValidationService],
	imports: [Layout, PanelInfoBarComponent, Inputs, DatePickerComponent, TimePickerComponent, TaskActionMailInputComponent, TaskActionLogComponent, TranslatePipe, CustomDatePipe]
})
@ComponentUrl('view-salesflow')
export class ViewSalesflowComponent {

	public taskAction: TaskActionModel | null = null;
	private latestLog: CommunicationLogEntryModel | null = null;
	public infoData: InfoBarModel[] = [];
	public communicationLog: CommunicationLogEntryModel[] = [];

	contacts: ContactModel[] = [];
	comments: string = '';
	headline: string = '';
	inputTypeEnum = InputType;
	allowFollowUp: boolean = false;
	requireFollowUpDate: boolean = false;
	requireFollowUpTime: boolean = false;
	followUp: boolean = true;

	followUpDate: Date | null = null;
	followUpTime: Time | null = null;

	@ViewChild('mailInput') mailInput!: TaskActionMailInputComponent;

	private _companyId?: string;
	public company?: CompanyModel;
	@Input() set companyId(value: string | undefined) {
		if (!value || value == this._companyId) return;
		this._companyId = value;
		this.companyService.getCompany(value).then(company => {
			this.company = company;
			this.contacts = company.companyContacts.map(cc => cc.contact!);
		});
	}
	get companyId() { return this._companyId; }

	private _taskflowId: string = '';
	private _taskflow?: TaskflowModel;
	@UrlParam
	@Input()
	set taskflowId(id: string) {
		this._taskflowId = id;
		this.reset();
		if (id != '') {
			this.taskService.getTaskflow(id).then(taskflow => this.loadTaskflow(taskflow));
			this.refreshLog();
		}
		else {
			this.loadTaskflow(new TaskflowModel);
			if (this.taskAction == null) {
				this.taskAction = new TaskActionModel();
				this.taskAction.type = TaskActionType.contact;
			}
		}
	}
	get taskflowId(): string {
		return this._taskflowId;
	}

	@Output() selectedActionIdChange = new EventEmitter<string>();

	actions = LogEntryStatus;

	private salesStatusTypes: string[] = [
		LogEntryStatus.meeting,
		LogEntryStatus.rescheduledMeeting,
		LogEntryStatus.offerSent,
		LogEntryStatus.dialog,
		LogEntryStatus.meetingCancelled,
		LogEntryStatus.noResponse,
		LogEntryStatus.rejected,
		LogEntryStatus.offerRejected,
		LogEntryStatus.mailOfferRejected,
		LogEntryStatus.createSale
	];
	private _nextAction?: string = undefined;
	set nextAction(action: string | undefined) {
		this._nextAction = action;
		switch (action) {
			case LogEntryStatus.meeting:
			case LogEntryStatus.rescheduledMeeting:
			case LogEntryStatus.offerSent:
				// If both date and time are "required", then a follow-up task is required, otherwise it is optional
				this.requireFollowUpDate = true;
				this.requireFollowUpTime = true;
				this.allowFollowUp = true;
				break;
			case LogEntryStatus.dialog:
			case LogEntryStatus.meetingCancelled:
			case LogEntryStatus.noResponse:
			case LogEntryStatus.offerRejected:
			case LogEntryStatus.mailOfferRejected:
				this.requireFollowUpDate = true;
				this.requireFollowUpTime = false;
				this.allowFollowUp = true;
				break;
			case LogEntryStatus.createSale:
			case LogEntryStatus.rejected:
				this.requireFollowUpDate = false;
				this.requireFollowUpTime = false;
				this.allowFollowUp = false;
				break;
		}
	}

	// Properties used to generate potential follow-up task and log communications
	get nextAction() { return this._nextAction; }
	get createFollowUpTask() { return (this.requireFollowUpDate && this.requireFollowUpTime) || (this.allowFollowUp && this.followUp); }
	private _permissionToEditTaskflow: boolean = false;
	get permissionToEditTaskflow() { return this._permissionToEditTaskflow; }
	private _permissionToEditTaskAction: boolean = false;
	get permissionToEditTaskAction() { return this._permissionToEditTaskAction; }

	constructor(
		private readonly navigation: NavigationService,
		private readonly taskService: TaskService,
		private readonly companyService: CompanyService,
		private readonly translate: TranslatePipe,
		private readonly customDate: CustomDatePipe,
		private readonly userService: UserService,
		private readonly communicationService: CommunicationService,
		private readonly notificationService: NotificationService) {
	}

	private reset() {
		this.taskAction = null;
		this.latestLog = null;
		this.comments = '';
		this.allowFollowUp = false;
		this._nextAction = undefined;
		this.followUpDate = null;
		this.followUpTime = null;
	}

	private loadTaskflow(taskflow: TaskflowModel): void {
		this._taskflowId = taskflow.id ? taskflow.id : '';
		this._taskflow = taskflow;

		this._permissionToEditTaskflow = false;
		this._permissionToEditTaskAction = false;

		this.companyId = taskflow.companyId;
		if (this._taskflow.id) {
			this.headline = (this._taskflow.completed) ? 'sale.completed-salesflow' : 'sale.active-salesflow';
		} else {
			this.headline = 'sale.new-salesflow';
		}

		const firstOpenTask = taskflow.taskActions.findIndex((taskAction) => { return !taskAction.completed; });

		if (firstOpenTask >= 0)
			this.taskAction = taskflow.taskActions[firstOpenTask];
		else
			this.taskAction = (taskflow.taskActions.length > 0) ? taskflow.taskActions[taskflow.taskActions.length - 1] : null;

		if (this.taskAction) {
			this.selectedActionIdChange.emit(this.taskAction.id);
			this.taskService.checkPermissionToEdit(taskflow, this.taskAction).then((access) => {
				this._permissionToEditTaskflow = access.taskflowAccess;
				this._permissionToEditTaskAction = access.taskActionAccess;
			});
		} else {
			this._permissionToEditTaskflow = true;
			this._permissionToEditTaskAction = true;
		}

		this.nextAction = LogEntryStatus.none;
		this.navigation.refreshUrl();
	}

	public async refreshLog() {
		if (!this._taskflowId) return;

		this.communicationLog = await this.communicationService.getLogEntriesForTaskflow(this._taskflowId);
		if (!this.communicationLog) return;

		this.communicationLog.sort((a, b) => (b.timestamp ? new Date(b.timestamp).getTime() : new Date(0).getTime()) - (a.timestamp ? new Date(a.timestamp).getTime() : new Date(0).getTime()));

		const recognizedStatusLogs = this.communicationLog.filter(entry => this.salesStatusTypes.indexOf(entry.statusType!) > -1);
		this.latestLog = recognizedStatusLogs.length > 0 ? recognizedStatusLogs[0] : null;

		this.infoData = [];
		if (this.latestLog) {
			let info = new InfoBarModel();
			info.icon = new InfoBarIconModel();
			info.icon.class = 'fa fa-square-full';
			info.icon.size = '10px';
			info.icon.color = this.taskService.getLogEntryStatusColor(this.latestLog.statusType);
			info.text = this.translate.transform(this.latestLog && this.latestLog.statusType ?
				'communicationlog.status.' + this.latestLog.statusType :
				'general.na');
			info.columnWidth = 2;
			this.infoData.push(info);

			info = new InfoBarModel();
			info.icon = new InfoBarIconModel();
			info.icon.class = 'far fa-calendar-alt';
			info.text = this.taskAction ? String(this.customDate.transform(this.taskAction.plannedDate, this.taskAction.dateOnly ? 'shortDate' : 'default')) : '';
			info.columnWidth = 2;
			this.infoData.push(info);

			info = new InfoBarModel();
			info.icon = new InfoBarIconModel();
			info.icon.class = 'fas fa-user-circle';
			info.text = this.userService.user(this.latestLog?.userId)?.name ?? this.translate.transform('general.na');
			info.columnWidth = 3;
			this.infoData.push(info);

			info = new InfoBarModel();
			info.icon = new InfoBarIconModel();
			info.icon.class = 'fas fa-file-alt';
			info.text = this.latestLog ? this.latestLog.comment : this.translate.transform('general.na');
			info.columnWidth = 5;
			this.infoData.push(info);
		}
	}

	setNextAction(action?: string) {
		if (!this.permissionToEditTaskAction) {
			this.notificationService.warning('taskflow.salesflow.missing-permissions-to-edit');
			return;
		}
		this.nextAction = action;
	}

	canSaveTaskChanges(): boolean {
		if (!this.followUp && (!this.requireFollowUpDate || !this.requireFollowUpTime)) return true;
		if ((!this.allowFollowUp && this.requireFollowUpDate) ||
			(this.requireFollowUpDate && !this.followUpDate) ||
			(this.requireFollowUpTime && (!this.followUpTime || this.followUpTime.currentTime === ''))) {
			return false;
		}
		return true;
	}

	saveTaskChanges() {
		if (this.taskActionIsContact()) {
			this.closeTaskAction();
		}
		if (this.taskActionIsMeeting()) {
			if (this._nextAction === LogEntryStatus.rescheduledMeeting) this.rescheduleTaskAction();
			if (this._nextAction === LogEntryStatus.meetingCancelled || this._nextAction === LogEntryStatus.offerRejected) this.closeTaskAction();
		}
	}

	closeTaskAction() {
		if (this.taskAction == null) return;

		let followUpDate = this.createFollowUpTask ? this.getFollowUp() : null;
		let taskType = this.nextAction === LogEntryStatus.meeting ? TaskActionType.meeting : TaskActionType.contact;
		let comments = this.comments;
		let mailData: string | null = null;
		if (this.nextAction === LogEntryStatus.offerSent) {
			let mail = this.mailInput.getMail();
			comments = mail.comment;
			mailData = JSON.stringify({ subject: mail.subject, recipients: mail.recipients });
		}

		this.taskAction.include(ta => ta.taskflow);
		if (this.taskflowId || this.taskflowId != '') {
			// If a salesflow is currently in process, close the existing task action, and create a new one if a follow-up has been set

			this.taskService.closeTaskAction(this.taskAction.id!, comments, this.nextAction, followUpDate, taskType, this.isDateOnly(), mailData).then(newTasks => {
				// If new task is created, load task, if not, reload current taskflow and show as closed
				this.taskflowId = (newTasks[0] && newTasks[0].taskflowId) ? newTasks[0].taskflowId : this.taskflowId;
			});
		} else {
			// If no salesflow is in process, create a new one based on the salesflow template and the chosen action

			if (this.companyId == undefined) return;
			this.taskService.createTaskActionWithNewTaskflow(this.comments, this.nextAction, followUpDate, taskType, 'salesflow', this.companyId, this.isDateOnly(), mailData)
				.then(taskAction => {
					this.taskflowId = (taskAction && taskAction.taskflowId) ? taskAction.taskflowId : '';
				});
		}
	};

	navigateToEditSale() {
		if (!this.permissionToEditTaskAction) {
			this.notificationService.warning(this.translate.transform('taskflow.salesflow.missing-permissions-to-edit'));
			return;
		}
		this.navigation.refreshView(EditSaleComponent).then(editSaleComponent => {
			editSaleComponent.companyId = this.companyId!;
			editSaleComponent.taskflowId = this.taskflowId;
		});
	}

	getFollowUp(): Date | null {
		if (!this.followUp && !(this.allowFollowUp && this.requireFollowUpDate && this.requireFollowUpTime)) return null;
		if (!this.followUpDate) return null;
		let date = new Date(this.followUpDate);
		if (this.followUpTime && this.followUpTime.currentTime !== '') {
			date.setHours(Number(this.followUpTime.hour), this.followUpTime.minute);
		}
		return date;
	}

	rescheduleTaskAction() {
		if (this.taskAction == null) return;
		if (this.getFollowUp() == null) return;

		this.taskAction.plannedDate = this.getFollowUp() ? this.getFollowUp() as Date : undefined;
		this.taskAction.dateOnly = this.isDateOnly();

		this.taskAction.include(ta => ta.taskflow);

		this.taskService.rescheduleTaskAction(this.taskAction, this.comments, this.nextAction).then(task => {
			if (task && task.taskflowId) this.taskflowId = task.taskflowId;
		});
	}

	taskActionIsContact(): boolean {
		return this.taskAction !== null && this.taskAction.type === TaskActionType.contact;
	}

	taskActionIsMeeting(): boolean {
		return this.taskAction != null && this.taskAction.type === TaskActionType.meeting;
	}

	taskActionIsOpen(): boolean {
		return this.taskAction != null ? !(this.taskAction.completed != undefined && this.taskAction.completed != null) : false;
	}

	statusIsOfferSent(): boolean {
		return this.latestLog ? this.latestLog.statusType === LogEntryStatus.offerSent : false;
	}

	isDateOnly(): boolean {
		return (!this.followUpTime || this.followUpTime.currentTime === '');
	}
}
